import React from 'react';
import PropTypes from 'prop-types';
import { LuAlertTriangle, LuCheckCircle2, LuPauseCircle } from 'react-icons/lu';
import { formatInZone } from './reminderFormat';

// Health of the reminder schedule (backend Reminders::Health): is the scheduler still
// ticking, and did a slot pass today without anyone being reminded?

export const needsAttention = (reminder) =>
  reminder.last_run?.status === 'failed' || (reminder.missed_today || []).length > 0;

/** Red when the 10-minute tick has stopped: then no reminder goes out at all. */
export const SchedulerHealthBanner = ({ scheduler }) => {
  if (!scheduler) return null;
  // Staging keeps the scheduler off on purpose: nothing is wrong, nothing is sent.
  if (!scheduler.healthy && scheduler.expected === false) {
    return (
      <p role="status" className="tw-m-0 tw-flex tw-items-start tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-lg tw-bg-slate-100 tw-text-[12px] tw-text-slate-700">
        <LuPauseCircle size={13} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
        <span>
          Scheduled sending is off on {scheduler.environment} by design (SIDEKIQ_SCHEDULER=false), so reminders never go out
          on their own here. Use <strong>Send test to me</strong> or <strong>Send now</strong> to try one.
        </span>
      </p>
    );
  }
  if (scheduler.healthy) {
    return (
      <p className="tw-m-0 tw-flex tw-items-center tw-gap-1.5 tw-text-[12px] tw-text-emerald-700">
        <LuCheckCircle2 size={13} aria-hidden="true" />
        Scheduler running · last check {formatInZone(scheduler.last_tick_at, undefined, true)}
      </p>
    );
  }
  return (
    <div role="alert" className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-lg tw-bg-red-50 tw-border tw-border-solid tw-border-red-200 tw-text-[13px] tw-text-red-800">
      <LuAlertTriangle size={15} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
      <span>
        <strong>Reminders are not running.</strong>{' '}
        {scheduler.last_tick_at
          ? `The scheduler last checked ${formatInZone(scheduler.last_tick_at, undefined, true)} (it should every 10 minutes).`
          : 'The scheduler has not checked in yet.'}{' '}
        Right after a deploy, wait 10 minutes. Otherwise make sure exactly one Sidekiq process runs with SIDEKIQ_SCHEDULER=true,
        then run <code>bin/rails reminders:check</code>.
      </span>
    </div>
  );
};
SchedulerHealthBanner.propTypes = {
  scheduler: PropTypes.shape({
    healthy: PropTypes.bool, expected: PropTypes.bool, environment: PropTypes.string, last_tick_at: PropTypes.string,
  }),
};

/** Today's slots that passed their whole window with no run. */
export const MissedSlotsAlert = ({ missed, timeZone }) => {
  if (!missed || missed.length === 0) return null;
  return (
    <div role="alert" className="tw-flex tw-items-start tw-gap-2 tw-px-3 tw-py-2.5 tw-rounded-lg tw-bg-red-50 tw-border tw-border-solid tw-border-red-200 tw-text-[13px] tw-text-red-800">
      <LuAlertTriangle size={15} className="tw-mt-0.5 tw-shrink-0" aria-hidden="true" />
      <span>
        <strong>Missed today:</strong>{' '}
        {missed.map((m) => `${m.label} (due ${formatInZone(m.at, timeZone)})`).join(', ')} never went out.
        Use <strong>Send now</strong> if it is still needed.
      </span>
    </div>
  );
};
MissedSlotsAlert.propTypes = {
  missed: PropTypes.arrayOf(PropTypes.shape({ slot: PropTypes.string, label: PropTypes.string, at: PropTypes.string })),
  timeZone: PropTypes.string,
};
